import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';

import SubscriptionStatusCard from './components/SubscriptionStatusCard';
import BillingHistoryTable from './components/BillingHistoryTable';
import VehicleCard from './components/VehicleCard';
import AccountSettingsSection from './components/AccountSettingsSection';
import AddVehicleModal from './components/AddVehicleModal';
import UpgradePlanModal from './components/UpgradePlanModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { generatePDF } from './components/UpgradePlanModal';

// Editorial section divider with ghost number
const EditorialSection = ({ number, label, children, accent = '#4A90E2' }) =>
<section style={{ position: 'relative', marginBottom: '80px' }}>
    {/* Ghost watermark number */}
    <div style={{
    position: 'absolute',
    top: '-20px',
    left: '-8px',
    fontFamily: 'Impact, Haettenschweiler, sans-serif',
    fontSize: 'clamp(5rem, 12vw, 9rem)',
    fontWeight: 900,
    color: 'rgba(74,144,226,0.04)',
    lineHeight: 1,
    userSelect: 'none',
    pointerEvents: 'none',
    letterSpacing: '-0.05em',
    zIndex: 0
  }}>{String(number)?.padStart(2, '0')}</div>

    {/* Section label row */}
    <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '0', marginBottom: '28px' }}>
      <div style={{
      background: accent,
      color: '#0D0D0D',
      padding: '6px 14px',
      fontFamily: 'Syne, sans-serif',
      fontSize: '0.625rem',
      fontWeight: 800,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 100%, 0 100%)'
    }}>{String(number)?.padStart(2, '0')}</div>
      <div style={{
      flex: 1,
      height: '2px',
      background: `linear-gradient(90deg, ${accent} 0%, rgba(74,144,226,0.1) 60%, transparent 100%)`
    }} />
      <span style={{
      fontFamily: 'Syne, sans-serif',
      fontSize: '0.6875rem',
      fontWeight: 800,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: accent,
      marginLeft: '16px'
    }}>{label}</span>
    </div>

    <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </section>;


// Handcrafted torn-paper divider
const TornDivider = ({ flip = false }) =>
<div style={{
  height: '24px',
  margin: '0 -56px',
  overflow: 'hidden',
  transform: flip ? 'scaleX(-1)' : 'none',
  opacity: 0.35
}}>
    <svg viewBox="0 0 1200 24" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
      <path d="M0,12 C50,4 80,20 130,8 C180,-4 210,18 260,10 C310,2 340,16 390,6 C440,-4 470,20 520,12 C570,4 600,18 650,8 C700,-2 730,16 780,10 C830,4 860,18 910,6 C960,-6 990,20 1040,12 C1090,4 1120,16 1200,8 L1200,24 L0,24 Z" fill="rgba(74,144,226,0.15)" />
      <path d="M0,16 C60,8 90,22 140,14 C190,6 220,20 270,12 C320,4 350,18 400,10 C450,2 480,16 530,8 C580,0 610,18 660,12 C710,6 740,20 790,14 C840,8 870,22 920,14 C970,6 1000,20 1050,12 C1100,4 1130,18 1200,12 L1200,24 L0,24 Z" fill="rgba(74,144,226,0.08)" />
    </svg>
  </div>;


const NordPassSubscriptionPortal = () => {
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingHistoryData, setBillingHistoryData] = useState([]);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const { user, userProfile, loading, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const [subRes, invoiceRes, vehicleRes] = await Promise.all([
        supabase?.from('subscriptions')?.select('*')?.eq('user_id', user?.id)?.single(),
        supabase?.from('invoices')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false }),
        supabase?.from('vehicles')?.select('*')?.eq('user_id', user?.id)?.order('is_primary', { ascending: false })]
        );
        if (subRes?.data) setSubscriptionData(subRes?.data);
        if (invoiceRes?.data) setBillingHistoryData(invoiceRes?.data);
        if (vehicleRes?.data) setVehiclesData(vehicleRes?.data);
      } catch (err) {
        console.error('Data fetch error:', err);
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleUpdatePayment = () => console.log('Update payment method');

  const handleUpgradePlan = async (plan, billingInfo) => {
    if (!user || !plan) return;
    setUpgradeLoading(true);
    try {
      const renewal = new Date();
      renewal?.setMonth(renewal?.getMonth() + 1);
      const renewalStr = renewal?.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })?.replace(/\./g, '/');
      const { error: subError } = await supabase?.from('subscriptions')?.update({
        plan_name: plan?.name, monthly_price: plan?.price,
        vehicles_included: plan?.vehicles, renewal_date: renewalStr,
        payment_method: 'Faktura', updated_at: new Date()?.toISOString()
      })?.eq('user_id', user?.id);
      if (subError) throw subError;
      const now = new Date();
      const dateStr = now?.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })?.replace(/\./g, '/');
      const invoiceNum = `NP-${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}${String(now?.getDate())?.padStart(2, '0')}-${Math.floor(Math.random() * 9000) + 1000}`;
      const monthNames = ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];
      const description = `${plan?.name} — ${monthNames?.[now?.getMonth()]} ${now?.getFullYear()}`;
      const { data: newInvoice, error: invError } = await supabase?.from('invoices')?.insert({
        user_id: user?.id, invoice_number: invoiceNum, date: dateStr,
        description, amount: plan?.price, status: plan?.priceNum === 0 ? 'paid' : 'pending'
      })?.select()?.single();
      if (invError) throw invError;
      if (plan?.priceNum > 0 && billingInfo) await generatePDF(billingInfo, plan, invoiceNum, dateStr);
      setSubscriptionData((prev) => ({ ...prev, plan_name: plan?.name, monthly_price: plan?.price, vehicles_included: plan?.vehicles, renewal_date: renewalStr, payment_method: 'Faktura' }));
      if (newInvoice) setBillingHistoryData((prev) => [newInvoice, ...prev]);
      setIsUpgradeModalOpen(false);
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleDownloadInvoice = async (id) => {
    const invoice = billingHistoryData?.find((i) => i?.id === id);
    if (!invoice) return;
    const content = `FAKTURA\n\nFakturanummer: ${invoice?.invoice_number}\nDato: ${invoice?.date}\nBeskrivelse: ${invoice?.description}\nBeløp: ${invoice?.amount}\nStatus: Betalt\n\nNordPass AS`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;a.download = `${invoice?.invoice_number}.txt`;a?.click();
    URL.revokeObjectURL(url);
  };

  const handleEditVehicle = (id) => console.log('Edit vehicle:', id);

  const handleDeleteVehicle = async (id) => {
    const { error } = await supabase?.from('vehicles')?.delete()?.eq('id', id);
    if (!error) setVehiclesData((prev) => prev?.filter((v) => v?.id !== id));
  };

  const handleSaveAccountSettings = async (data) => {
    const updates = {
      first_name: data?.firstName, last_name: data?.lastName, phone: data?.phone,
      notification_email: data?.notifications?.email, notification_sms: data?.notifications?.sms,
      notification_traffic: data?.notifications?.traffic, privacy_data_processing: data?.privacy?.dataProcessing,
      privacy_marketing: data?.privacy?.marketing, privacy_data_sharing: data?.privacy?.dataSharing
    };
    return await updateProfile(updates);
  };

  const handleAddVehicle = async (data) => {
    const { data: newVehicle, error } = await supabase?.from('vehicles')?.insert({
      user_id: user?.id, make: data?.make, model: data?.model, year: data?.year,
      registration_number: data?.registrationNumber, emission_classification: data?.emissionClassification || 'standard',
      discount_eligible: data?.discountEligible || false, discount_percentage: data?.discountPercentage || '0%',
      is_primary: vehiclesData?.length === 0
    })?.select()?.single();
    if (!error && newVehicle) setVehiclesData((prev) => [...prev, newVehicle]);
  };

  const accountData = {
    firstName: userProfile?.first_name || '',
    lastName: userProfile?.last_name || '',
    email: userProfile?.email || user?.email || '',
    phone: userProfile?.phone || '',
    notifications: {
      email: userProfile?.notification_email ?? true,
      sms: userProfile?.notification_sms ?? true,
      traffic: userProfile?.notification_traffic ?? false
    },
    privacy: {
      dataProcessing: userProfile?.privacy_data_processing ?? true,
      marketing: userProfile?.privacy_marketing ?? false,
      dataSharing: userProfile?.privacy_data_sharing ?? true
    }
  };

  const initials = `${accountData?.firstName?.[0] ?? ''}${accountData?.lastName?.[0] ?? ''}`;

  const subDisplay = subscriptionData ? {
    planName: subscriptionData?.plan_name, status: subscriptionData?.status,
    renewalDate: subscriptionData?.renewal_date, paymentMethod: subscriptionData?.payment_method,
    monthlyPrice: subscriptionData?.monthly_price, vehiclesIncluded: subscriptionData?.vehicles_included
  } : {
    planName: 'NordPass Premium', status: 'active', renewalDate: '—',
    paymentMethod: '—', monthlyPrice: '—', vehiclesIncluded: 'Opptil 5 kjøretøy'
  };

  const vehiclesDisplay = vehiclesData?.map((v) => ({
    id: v?.id, make: v?.make, model: v?.model, year: v?.year,
    registrationNumber: v?.registration_number, emissionClassification: v?.emission_classification,
    discountEligible: v?.discount_eligible, discountPercentage: v?.discount_percentage,
    isPrimary: v?.is_primary, image: v?.image, imageAlt: v?.image_alt
  }));

  const invoicesDisplay = billingHistoryData?.map((inv) => ({
    id: inv?.id, invoiceNumber: inv?.invoice_number, date: inv?.date,
    description: inv?.description, amount: inv?.amount, status: inv?.status
  }));

  if (loading || dataLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0C10', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
        <div style={{
          width: '48px', height: '48px',
          border: '3px solid rgba(74,144,226,0.15)',
          borderTop: '3px solid #4A90E2',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <span style={{ fontFamily: 'Syne, sans-serif', color: '#8A95A3', fontSize: '0.8125rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Laster konto</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>);

  }

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>Min konto — NordPass</title>
        <meta name="description" content="Administrer ditt NordPass Premium-abonnement, last ned fakturaer, og håndter kjøretøyprofiler." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
      </Helmet>

      <div className="min-h-screen" style={{ background: '#0A0C10' }}>
        <Header />

        {/* ═══════════════════════════════════════════════════════════
             EDITORIAL HERO — Norwegian fjord landscape + identity block
          ═══════════════════════════════════════════════════════════ */}
        <div style={{ position: 'relative', overflow: 'hidden', minHeight: '420px' }}>

          {/* Full-bleed background image */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1168630fb-1771465106542.png",
            backgroundSize: 'cover',
            backgroundPosition: 'center 40%',
            filter: 'brightness(0.28) saturate(0.7) contrast(1.1)'
          }} />

          {/* Diagonal color overlay — fjord blue */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg, rgba(10,12,16,0.92) 0%, rgba(26,46,82,0.6) 50%, rgba(10,12,16,0.85) 100%)'
          }} />

          {/* Diagonal cut accent — aurora orange */}
          <div style={{
            position: 'absolute',
            top: 0, right: 0,
            width: '35%', height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(245,166,35,0.06) 100%)',
            clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)'
          }} />

          {/* Vertical rule — editorial accent */}
          <div style={{
            position: 'absolute',
            left: '56px', top: '40px', bottom: '40px',
            width: '2px',
            background: 'linear-gradient(to bottom, transparent, #4A90E2 20%, #4A90E2 80%, transparent)'
          }} />

          {/* Content */}
          <div className="max-w-container mx-auto" style={{ padding: '64px 56px 56px', position: 'relative', zIndex: 1 }}>

            {/* Eyebrow label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
              <div style={{
                width: '32px', height: '32px',
                background: '#4A90E2',
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                flexShrink: 0
              }} />
              <span style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.625rem',
                fontWeight: 800,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: '#4A90E2'
              }}>Min konto / NordPass Portal</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(74,144,226,0.25)' }} />
              {/* Live session badge */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(46,196,160,0.08)',
                border: '1px solid rgba(46,196,160,0.2)',
                padding: '4px 12px'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: '#2EC4A0',
                  boxShadow: '0 0 8px rgba(46,196,160,0.8)',
                  animation: 'pulse 2s ease-in-out infinite'
                }} />
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.6875rem', color: '#2EC4A0', fontWeight: 500 }}>Aktiv økt</span>
              </div>
            </div>

            {/* Main identity block — asymmetric */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '40px', alignItems: 'end', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                {/* Name — Impact-style brutalist */}
                <h1 style={{
                  fontFamily: 'Impact, Haettenschweiler, "Arial Narrow", sans-serif',
                  fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                  fontWeight: 900,
                  color: '#EEF0F2',
                  letterSpacing: '-0.02em',
                  lineHeight: 0.95,
                  margin: '0 0 8px',
                  textTransform: 'uppercase'
                }}>
                  {accountData?.firstName || 'Bruker'}
                  <span style={{
                    display: 'block',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: '0.45em',
                    color: '#F5A623',
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    marginTop: '4px'
                  }}>{accountData?.lastName}</span>
                </h1>

                {/* Email — monospace editorial */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                  <div style={{
                    width: '20px', height: '1px',
                    background: '#4A90E2'
                  }} />
                  <span style={{
                    fontFamily: 'JetBrains Mono, "Courier New", monospace',
                    fontSize: '0.875rem',
                    color: '#8A95A3',
                    letterSpacing: '0.02em'
                  }}>{accountData?.email}</span>
                </div>
              </div>

              {/* Right column — polaroid-style identity card */}
              <div style={{
                background: '#EEF0F2',
                padding: '16px 16px 24px',
                transform: 'rotate(1.5deg)',
                boxShadow: '4px 4px 0 rgba(0,0,0,0.5), 8px 8px 24px rgba(0,0,0,0.4)',
                minWidth: '140px',
                textAlign: 'center',
                flexShrink: 0
              }}>
                {/* Avatar */}
                <div style={{
                  width: '80px', height: '80px',
                  background: 'linear-gradient(135deg, #4A90E2, #6B5CE7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px',
                  border: '3px solid #0A0C10'
                }}>
                  <span style={{
                    fontFamily: 'Impact, sans-serif',
                    fontWeight: 900,
                    fontSize: '1.75rem',
                    color: '#fff',
                    letterSpacing: '-0.02em'
                  }}>{initials || '?'}</span>
                </div>
                <div style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '0.6875rem',
                  color: '#1A1A1A',
                  fontStyle: 'italic',
                  lineHeight: 1.4
                }}>
                  {subDisplay?.planName}
                </div>
                <div style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.5625rem',
                  fontWeight: 800,
                  color: '#4A90E2',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  marginTop: '4px'
                }}>NordPass</div>
              </div>
            </div>

            {/* Stats strip — horizontal scrapbook */}
            <div style={{
              display: 'flex',
              gap: '0',
              marginTop: '40px',
              borderTop: '1px solid rgba(74,144,226,0.2)',
              paddingTop: '24px',
              flexWrap: 'wrap'
            }}>
              {[
              { num: vehiclesDisplay?.length, label: 'Kjøretøy', unit: 'av 5', color: '#2EC4A0' },
              { num: invoicesDisplay?.length, label: 'Fakturaer', unit: 'totalt', color: '#4A90E2' },
              { num: subDisplay?.monthlyPrice, label: 'Månedlig', unit: 'NOK', color: '#F5A623' },
              { num: 'SSL', label: 'Kryptert', unit: '256-bit', color: '#6B5CE7' }]?.
              map((stat, i) =>
              <div key={i} style={{
                flex: '1 1 120px',
                padding: '0 24px',
                borderRight: i < 3 ? '1px solid rgba(138,149,163,0.12)' : 'none'
              }}>
                  <div style={{
                  fontFamily: 'Impact, Haettenschweiler, sans-serif',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 900,
                  color: stat?.color,
                  letterSpacing: '-0.03em',
                  lineHeight: 1
                }}>{stat?.num}</div>
                  <div style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  color: '#8A95A3',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  marginTop: '2px'
                }}>{stat?.label} <span style={{ color: 'rgba(138,149,163,0.5)' }}>/ {stat?.unit}</span></div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom diagonal cut */}
          <div style={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            height: '40px',
            background: '#0A0C10',
            clipPath: 'polygon(0 100%, 100% 100%, 100% 0)'
          }} />
        </div>

        {/* ═══════════════════════════════════════════════════════════
             MAIN CONTENT — editorial magazine layout
          ═══════════════════════════════════════════════════════════ */}
        <main className="max-w-container mx-auto" style={{ padding: '64px 56px 96px' }}>

          <EditorialSection number={1} label="Abonnement" accent="#4A90E2">
            <SubscriptionStatusCard
              subscription={subDisplay}
              onUpdatePayment={handleUpdatePayment}
              onUpgradePlan={() => setIsUpgradeModalOpen(true)} />
            
          </EditorialSection>

          <TornDivider />

          <EditorialSection number={2} label="Fakturahistorikk" accent="#6B5CE7">
            <BillingHistoryTable billingHistory={invoicesDisplay} onDownloadInvoice={handleDownloadInvoice} />
          </EditorialSection>

          <TornDivider flip />

          <EditorialSection number={3} label="Mine kjøretøy" accent="#F5A623">
            {/* Section heading — asymmetric */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              marginBottom: '32px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <h2 style={{
                  fontFamily: 'Impact, Haettenschweiler, "Arial Narrow", sans-serif',
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  fontWeight: 900,
                  color: '#EEF0F2',
                  letterSpacing: '-0.02em',
                  lineHeight: 0.95,
                  textTransform: 'uppercase',
                  margin: 0
                }}>
                  Registrerte
                  <span style={{
                    display: 'block',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: '0.55em',
                    color: '#F5A623',
                    textTransform: 'none',
                    letterSpacing: '0.01em'
                  }}>kjøretøy</span>
                </h2>
              </div>
              <button
                onClick={() => setIsAddVehicleModalOpen(true)}
                style={{
                  background: '#F5A623',
                  color: '#0A0C10',
                  padding: '13px 24px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  boxShadow: '4px 4px 0 rgba(245,166,35,0.3)',
                  transition: 'all 150ms ease',
                  clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
                }}
                onMouseEnter={(e) => {e.currentTarget.style.transform = 'translate(-2px,-2px)';e.currentTarget.style.boxShadow = '6px 6px 0 rgba(245,166,35,0.3)';}}
                onMouseLeave={(e) => {e.currentTarget.style.transform = 'translate(0,0)';e.currentTarget.style.boxShadow = '4px 4px 0 rgba(245,166,35,0.3)';}}>
                
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A0C10" strokeWidth="3" strokeLinecap="square">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Legg til kjøretøy
              </button>
            </div>

            {vehiclesDisplay?.length === 0 ?
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: '#111318',
              border: '1px solid rgba(245,166,35,0.1)',
              borderTop: '3px solid #F5A623'
            }}>
                <div style={{
                width: '56px', height: '56px',
                background: 'rgba(245,166,35,0.08)',
                border: '2px solid rgba(245,166,35,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F5A623" strokeWidth="1.5">
                    <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v5" />
                    <circle cx="15.5" cy="17.5" r="2.5" />
                    <circle cx="4.5" cy="17.5" r="2.5" />
                  </svg>
                </div>
                <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#8A95A3', fontSize: '1rem' }}>Ingen kjøretøy registrert ennå</p>
                <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(138,149,163,0.5)', fontSize: '0.8125rem', marginTop: '4px' }}>Legg til ditt første kjøretøy ovenfor</p>
              </div> :

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px', alignItems: 'start' }}>
                {vehiclesDisplay?.map((vehicle, index) =>
              <VehicleCard key={vehicle?.id} vehicle={vehicle} onEdit={handleEditVehicle} onDelete={handleDeleteVehicle} index={index} />
              )}
              </div>
            }
          </EditorialSection>

          <TornDivider />

          <EditorialSection number={4} label="Kontoinnstillinger" accent="#6B5CE7">
            <AccountSettingsSection accountData={accountData} onSave={handleSaveAccountSettings} signOut={signOut} />
          </EditorialSection>

          {/* Footer trust strip */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '32px',
            flexWrap: 'wrap',
            padding: '28px 0',
            borderTop: '1px solid rgba(138,149,163,0.08)',
            marginTop: '16px'
          }}>
            {[
            { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', label: '256-bit kryptering' },
            { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', label: 'GDPR-beskyttet' },
            { icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14', label: 'PCI DSS-sertifisert' }]?.
            map((item, i) =>
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round">
                  <path d={item?.icon} />
                  {i === 2 && <polyline points="9 11 12 14 22 4" />}
                </svg>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#8A95A3' }}>{item?.label}</span>
              </div>
            )}
          </div>
        </main>

        {/* Editorial footer */}
        <footer style={{ borderTop: '2px solid rgba(74,144,226,0.15)', background: '#070809' }}>
          <div className="max-w-container mx-auto" style={{ padding: '32px 56px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '24px', height: '24px',
                  background: '#4A90E2',
                  clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                }} />
                <span style={{
                  fontFamily: 'Impact, Haettenschweiler, sans-serif',
                  fontWeight: 900,
                  color: '#EEF0F2',
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}>NORDPASS</span>
              </div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#8A95A3' }}>© 2026 NordPass AS</p>
              <div style={{ display: 'flex', gap: '24px' }}>
                {['Personvern', 'Vilkår', 'Kontakt']?.map((item) =>
                <button key={item} style={{
                  fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem',
                  color: '#8A95A3', background: 'none', border: 'none',
                  cursor: 'pointer', padding: 0, transition: 'color 200ms ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#EEF0F2'}
                onMouseLeave={(e) => e.target.style.color = '#8A95A3'}>
                    {item}
                  </button>
                )}
              </div>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <AddVehicleModal isOpen={isAddVehicleModalOpen} onClose={() => setIsAddVehicleModalOpen(false)} onAdd={handleAddVehicle} />
      <UpgradePlanModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentPlan={subDisplay?.planName}
        onUpgrade={handleUpgradePlan}
        isLoading={upgradeLoading}
        userEmail={accountData?.email} />
      
    </>);

};

export default NordPassSubscriptionPortal;